// src/lib/memberColors.ts — a colour per person.
//
// "Random" per member, but derived from their member id rather than actually
// random: the same person has to keep the same colour across every screen, and
// across everyone else's phone. A real random pick would reshuffle on each
// render, so Sam would be amber in the roster and teal in the expense list.
//
// The palette is hand-picked rather than generated from a hue wheel. Evenly
// spaced hues at fixed saturation produce muddy olives and shrill yellows that
// fight the accent; these are chosen to sit beside #0E7A63 without competing,
// and each has a tint for the tile behind it.

export interface MemberColor {
  /** The glyph / initial colour. Passes contrast on its own tint. */
  ink: string;
  /** The tile behind it. */
  tint: string;
}

const PALETTE: MemberColor[] = [
  { ink: '#0E7A63', tint: '#DDEDE8' }, // the accent itself
  { ink: '#2F6DB0', tint: '#DDE8F4' }, // blue
  { ink: '#B4632A', tint: '#F6E7D8' }, // amber
  { ink: '#8B4E9C', tint: '#EEE1F2' }, // plum
  { ink: '#B03A5B', tint: '#F7DEE5' }, // rose
  { ink: '#3F7A47', tint: '#E1EFE2' }, // leaf
  { ink: '#2A7B8C', tint: '#DCEDF1' }, // teal
  { ink: '#7A6A2E', tint: '#EFEADA' }, // olive
];

/**
 * FNV-1a. Any stable hash would do; this one is four lines and has no
 * clustering on the short hex-ish strings that uuids start with, which a naive
 * charCode sum very much does.
 */
function hash(value: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function colorForMember(memberId: string): MemberColor {
  return PALETTE[hash(memberId) % PALETTE.length];
}

/**
 * One or two letters for the tile. Takes the first letter of the first and
 * last words, so "Nadia K" reads NK — which is what distinguishes two people
 * whose colours happen to collide in a group larger than the palette.
 */
export function initialsOf(displayName: string): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 1).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}
